// attributeReviewConfig.ts
// Palette, layout constants, and dataset-colouring helper for AttributeReview.
// Extracted so the main component stays within the 300-line limit.

export type PaletteEntry = { solid: string; bar: string };

// Left axis  → navy / cornflower blue family
// Right axis → slate-teal family
// Always applied — API colours are ignored to prevent neon overrides.
const root = getComputedStyle(document.documentElement);
const get = (v: string) => root.getPropertyValue(v).trim();

export const leftPalette: PaletteEntry[] = [
  { solid: get('--primary-700'), bar: 'rgba(14,37,144,0.75)' },
  { solid: get('--primary-500'), bar: 'rgba(18,10,143,0.70)' },
  { solid: get('--primary-300'), bar: 'rgba(98,119,220,0.68)' },
  { solid: get('--primary-100'), bar: 'rgba(182,192,239,0.65)' },
];

export const rightPalette: PaletteEntry[] = [
  { solid: get('--success-600'), bar: 'rgba(75,178,24,0.75)' },
  { solid: get('--success-500'), bar: 'rgba(82,196,26,0.70)' },
  { solid: get('--success-400'), bar: 'rgba(117,208,72,0.68)' },
  { solid: get('--success-200'), bar: 'rgba(175,228,150,0.65)' },
];

export const labelColWidth = 160;
export const rightAxisWidth = 160;
export const colWidth = 190;
export const chartHeight = 380;

export const tagBaseStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 12px 4px 8px',
  borderRadius: 5,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.01em',
  lineHeight: '20px',
};

/** Assign axis IDs + force professional colours onto every dataset. */
export const buildAssignedDatasets = (rawDatasets: any[]): any[] => {
  let lIdx = 0;
  let rIdx = 0;

  return rawDatasets.map((ds, idx) => {
    const half = Math.ceil(rawDatasets.length / 2);
    const axisId = ds.yAxisID ?? (idx < half ? 'y' : 'y1');
    const isLeft = axisId === 'y';

    let col: PaletteEntry;
    if (isLeft) {
      col = leftPalette[lIdx % leftPalette.length];
      lIdx += 1;
    } else {
      col = rightPalette[rIdx % rightPalette.length];
      rIdx += 1;
    }

    const isLine = !isLeft; // right axis → line, left axis → bar

    return {
      ...ds,
      type: isLine ? 'line' : 'bar', // ← mixed chart type
      yAxisID: axisId,
      backgroundColor: isLine ? 'transparent' : col.bar,
      borderColor: col.solid,
      borderWidth: isLine ? 2.5 : 1.5,
      borderRadius: isLine ? 0 : 5,
      borderSkipped: false,
      hoverBackgroundColor: col.solid,
      hoverBorderColor: col.solid,
      // Line-specific options
      ...(isLine && {
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: col.solid,
        pointBorderColor: get('--neutral-10'),
        pointBorderWidth: 2,
        tension: 0, // slight curve
        fill: false,
      }),
      // Bar-specific options
      ...(!isLine && {
        barPercentage: 0.65,
        categoryPercentage: 0.75,
      }),
      solidColor: col.solid,
    };
  });
};

/** Build Chart.js scale options for left + optional right axis. */
export const buildScales = (
  leftLabel: string,
  rightLabel: string,
  leftColor: string,
  rightColor: string,
  hasSecondAxis: boolean
) => ({
  x: {
    stacked: false,
    grid: { display: false },
    border: { display: false },
    ticks: {
      color: get('--neutral-300'), // ← slightly lighter, matches reference
      font: { size: 13, weight: '500' as const },
      maxRotation: 0, // ← keep labels horizontal
      minRotation: 0,
    },
  },
  y: {
    type: 'linear' as const,
    position: 'left' as const,
    grid: {
      color: 'rgba(0,0,0,0.06)', // ← lighter grid lines like reference
      lineWidth: 1,
      drawBorder: false,
    },
    border: { display: false },
    ticks: {
      color: leftColor, // ← LEFT axis numbers colored to match left dataset
      font: { size: 12, weight: '500' as const },
      padding: 8, // ← breathing room between number and axis
    },

    afterFit: (scale: { width: number }) => {
      // eslint-disable-next-line no-param-reassign
      scale.width = labelColWidth;
    },
  },
  ...(hasSecondAxis && {
    y1: {
      type: 'linear' as const,
      position: 'right' as const,
      grid: { drawOnChartArea: false },
      border: { display: false },
      ticks: {
        color: rightColor, // ← RIGHT axis numbers colored to match right dataset
        font: { size: 12, weight: '500' as const },
        padding: 8, // ← consistent padding
      },

      afterFit: (scale: { width: number }) => {
        // eslint-disable-next-line no-param-reassign
        scale.width = rightAxisWidth;
      },
    },
  }),
});
